import { AuthenticationType, RouterName } from '../constants';

export const RETURN_TO_PARAM = 'returnTo';

const INTERNAL_ORIGIN = 'https://dashboard.invalid';

type ParsedDestination = {
  decodedPathname: string;
  url: URL;
};

const removeTrailingSlash = (pathname: string): string => {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.replace(/\/+$/, '');
};

const decodePathname = (pathname: string): string | null => {
  try {
    let decoded = pathname;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const nextValue = decodeURIComponent(decoded);

      if (nextValue === decoded) {
        break;
      }

      decoded = nextValue;
    }

    return decoded;
  } catch {
    return null;
  }
};

const parseInternalDestination = (
  candidate: string | null | undefined,
): ParsedDestination | null => {
  if (
    !candidate ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes('\\')
  ) {
    return null;
  }

  const pathEnd = candidate.search(/[?#]/);
  const rawPathname = pathEnd === -1 ? candidate : candidate.slice(0, pathEnd);
  const decodedPathname = decodePathname(rawPathname);

  if (
    !decodedPathname ||
    decodedPathname.startsWith('//') ||
    decodedPathname.includes('\\') ||
    decodedPathname.includes('//')
  ) {
    return null;
  }

  const hasTraversal = decodedPathname
    .split('/')
    .some((segment) => segment === '.' || segment === '..');

  if (hasTraversal) {
    return null;
  }

  const url = new URL(candidate, INTERNAL_ORIGIN);

  if (url.origin !== INTERNAL_ORIGIN) {
    return null;
  }

  const normalizedPathname = decodePathname(url.pathname);

  if (!normalizedPathname || normalizedPathname.includes('\\')) {
    return null;
  }

  return {
    decodedPathname: removeTrailingSlash(normalizedPathname),
    url,
  };
};

const isMfaDestination = (pathname: string): boolean => {
  if (
    pathname === RouterName.create ||
    pathname === RouterName.manage ||
    pathname === RouterName.account ||
    pathname === RouterName.help
  ) {
    return true;
  }

  return /^\/manage\/documents\/[^/]+$/.test(pathname);
};

const isSFADestination = (pathname: string): boolean =>
  pathname === RouterName.dashboard || pathname === RouterName.help;

const isKnownAuthenticatedDestination = (pathname: string): boolean =>
  isMfaDestination(pathname) || isSFADestination(pathname);

/**
 * Returns the existing default destination for an authenticated user.
 *
 * @param authenticationType - The user's current authentication mode.
 * @returns The default route configured for the authentication mode.
 */
export const getDefaultAuthenticatedDestination = (
  authenticationType: string,
): string =>
  authenticationType === AuthenticationType.MFA
    ? RouterName.create
    : RouterName.dashboard;

/**
 * Derives an optional return destination from a protected request.
 *
 * @param pathname - The requested pathname.
 * @param search - The requested query string, including its leading question mark.
 * @param hash - The requested hash, including its leading hash character.
 * @returns A safe non-default destination, or undefined when none is meaningful.
 */
export const deriveReturnTo = (
  pathname: string,
  search = '',
  hash = '',
): string | undefined => {
  const parsed = parseInternalDestination(`${pathname}${search}${hash}`);

  if (!parsed || parsed.decodedPathname === '/') {
    return undefined;
  }

  if (!isKnownAuthenticatedDestination(parsed.decodedPathname)) {
    return undefined;
  }

  const isDefaultDestination =
    (parsed.decodedPathname === RouterName.create ||
      parsed.decodedPathname === RouterName.dashboard) &&
    !parsed.url.search &&
    !parsed.url.hash;

  if (isDefaultDestination) {
    return undefined;
  }

  return `${parsed.url.pathname}${parsed.url.search}${parsed.url.hash}`;
};

/**
 * Builds the login URL for a requested application location.
 *
 * @param pathname - The requested pathname.
 * @param search - The requested query string, including its leading question mark.
 * @param hash - The requested hash, including its leading hash character.
 * @returns The login route with an optional encoded return-to destination.
 */
export const getLoginDestination = (
  pathname: string,
  search = '',
  hash = '',
): string => {
  const returnTo = deriveReturnTo(pathname, search, hash);

  if (!returnTo) {
    return RouterName.login;
  }

  const loginSearch = new URLSearchParams({
    [RETURN_TO_PARAM]: returnTo,
  });

  return `${RouterName.login}?${loginSearch.toString()}`;
};

/**
 * Resolves unverified return-to input to a route available to the user's
 * authentication mode.
 *
 * @param candidate - The unverified return-to value.
 * @param authenticationType - The user's current authentication mode.
 * @returns The safe destination or the mode's default authenticated route.
 */
export const resolveAuthenticatedDestination = (
  candidate: string | null | undefined,
  authenticationType: string,
): string => {
  const fallback = getDefaultAuthenticatedDestination(authenticationType);
  const parsed = parseInternalDestination(candidate);

  if (!parsed) {
    return fallback;
  }

  const isAllowed =
    authenticationType === AuthenticationType.MFA
      ? isMfaDestination(parsed.decodedPathname)
      : isSFADestination(parsed.decodedPathname);

  if (!isAllowed) {
    return fallback;
  }

  if (
    parsed.decodedPathname === fallback &&
    !parsed.url.search &&
    !parsed.url.hash
  ) {
    return fallback;
  }

  return `${parsed.url.pathname}${parsed.url.search}${parsed.url.hash}`;
};
